
import { Question } from '../types';

export const qur101PreservationQuestions: Question[] = [
  {
    id: 'qur071',
    text: 'What happened when multiple narrations exist for the reason of revelation for Sūrat ud-Ḍuḥā (93)?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Multiple authentic narrations describe different aspects of the same event', isCorrect: true },
      { id: 'b', text: 'The surah may have been revealed more than once', isCorrect: true },
      { id: 'c', text: 'One revelation may have had multiple simultaneous causes', isCorrect: true },
      { id: 'd', text: 'Only one narration is accepted, the others rejected', isCorrect: false }
    ],
    explanation: 'For Sūrat ud-Ḍuḥā (93), multiple equally authentic narrations exist: one mentions the Prophet ﷺ being upset for three days because Jibrīl didn\'t visit and someone commented "your devil has not visited you"; another describes him searching his home for something preventing Jibrīl\'s visit. These narrations are considered complementary aspects of the same event or indicate the surah may have been revealed more than once.',
    topic: 'qur101'
  },
  {
    id: 'qur072',
    text: 'What should scholars do when there are multiple reports of equal authenticity about the same verse\'s revelation?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Consider that the verse may have been revealed multiple times', isCorrect: true },
      { id: 'b', text: 'Consider that a ruling may have applied to multiple similar situations', isCorrect: true },
      { id: 'c', text: 'Recognize that repetition helps institutionalize and embed rulings', isCorrect: true },
      { id: 'd', text: 'Always prioritize the narration from the oldest companion', isCorrect: false }
    ],
    explanation: 'When multiple reports of equal authenticity exist about a verse\'s revelation, scholars should: consider that the verse may have been revealed multiple times (first as revelation, later as a reminder); recognize that a ruling may have applied to multiple similar situations; and understand that repetition helps institutionalize and embed rulings in the community. The Qur\'ān isn\'t repetitive, but rulings may be repeated for emphasis and cultural embedding.',
    topic: 'qur101'
  },
  {
    id: 'qur073',
    text: 'What is the practical definition of Jam\'ul Qur\'ān (preservation of the Qur\'ān)?',
    type: 'single',
    options: [
      { id: 'a', text: 'The preservation and protection of the Qur\'ān', isCorrect: true },
      { id: 'b', text: 'The memorization techniques used for the Qur\'ān', isCorrect: false },
      { id: 'c', text: 'The science of Qur\'ānic recitation', isCorrect: false },
      { id: 'd', text: 'The translation of the Qur\'ān into other languages', isCorrect: false }
    ],
    explanation: 'The practical definition (iṣṭilāḥan) of Jam\'ul Qur\'ān is "the preservation and protection of the Qur\'ān." While linguistically (lughatan) it means "to gather or collect something," in scholarly discourse it refers to the entire process of safeguarding the Qur\'ān through memorization, writing, compilation, and standardization.',
    topic: 'qur101'
  },
  {
    id: 'qur074',
    text: 'What were the main eras of Qur\'ānic preservation?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Era of memorization during Prophet Muhammad\'s ﷺ time', isCorrect: true },
      { id: 'b', text: 'Era of formalization during Abu Bakr\'s time', isCorrect: true },
      { id: 'c', text: 'Era of standardization during Uthman\'s time', isCorrect: true },
      { id: 'd', text: 'Era of clarification during Ali\'s time', isCorrect: false }
    ],
    explanation: 'The main eras of Qur\'ānic preservation were: (1) The time of memorization (ḥifẓ) during Prophet Muhammad\'s ﷺ lifetime; (2) The time of formalization during Abu Bakr\'s caliphate, when the Qur\'ān was first compiled into an official manuscript; and (3) The time of standardization during Uthman\'s caliphate, when multiple standardized copies were made and distributed to unify recitation.',
    topic: 'qur101'
  },
  {
    id: 'qur075',
    text: 'What were the primary methods of Qur\'ānic preservation during the Prophet\'s ﷺ time?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Memorization (Ḥifẓ)', isCorrect: true },
      { id: 'b', text: 'Writing on various materials', isCorrect: true },
      { id: 'c', text: 'Regular recitation and review', isCorrect: true },
      { id: 'd', text: 'Printing on paper', isCorrect: false }
    ],
    explanation: 'During the Prophet\'s ﷺ time, the primary methods of Qur\'ānic preservation were: memorization (Ḥifẓ), which was the main method with special companions designated as preservers; writing on various materials (bones, leaves, stones, parchment, etc.); and regular recitation and review, with the Prophet ﷺ instructing companions to review the Qur\'ān monthly.',
    topic: 'qur101'
  },
  {
    id: 'qur076',
    text: 'What did literacy mean in the historical context of early Islam?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'The ability to understand and transmit oral traditions', isCorrect: true },
      { id: 'b', text: 'Possessing knowledge within historical oral traditions', isCorrect: true },
      { id: 'c', text: 'Not necessarily the ability to read and write', isCorrect: true },
      { id: 'd', text: 'The same as modern literacy (reading and writing)', isCorrect: false }
    ],
    explanation: 'In the historical context of early Islam, literacy had a different meaning than today. It referred more to the ability to understand and transmit oral traditions and possessing knowledge within historical oral contexts. Reading and writing weren\'t the focus of education; people were taught and learned via oral tradition. This is why the Prophet Muhammad ﷺ is described as "unlettered" rather than "illiterate"—he had high levels of literacy within that historical context but couldn\'t read or write.',
    topic: 'qur101'
  },
  {
    id: 'qur077',
    text: 'What was the literacy situation in early Makkah and Yathrib (pre-Madinah)?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Many Quraishis in Makkah could read and write', isCorrect: true },
      { id: 'b', text: 'Abu Bakr, Umar, Uthman, and Ali could all read and write', isCorrect: true },
      { id: 'c', text: 'Different tribes in Yathrib had different literacy priorities', isCorrect: true },
      { id: 'd', text: 'No one in Arabia could read or write before Islam', isCorrect: false }
    ],
    explanation: 'In early Makkah, many Quraishis could read and write, including Abu Bakr, Umar, Uthman, Ali, and their children. This is why it was surprising that the Prophet ﷺ couldn\'t read or write despite his intelligence and status. In Yathrib (pre-Madinah), different tribes had different skills and priorities regarding literacy, with some placing higher emphasis on reading and writing than others.',
    topic: 'qur101'
  },
  {
    id: 'qur078',
    text: 'What event prompted Abu Bakr RA to compile the Qur\'ān?',
    type: 'single',
    options: [
      { id: 'a', text: 'The martyrdom of many Qur\'ān memorizers at the Battle of Yamāmah', isCorrect: true },
      { id: 'b', text: 'The Prophet\'s ﷺ final instruction before his death', isCorrect: false },
      { id: 'c', text: 'Disagreements about certain verses among companions', isCorrect: false },
      { id: 'd', text: 'The conversion of many non-Arabs who couldn\'t memorize easily', isCorrect: false }
    ],
    explanation: 'Abu Bakr RA was prompted to compile the Qur\'ān after the Battle of Yamāmah, where approximately 70 ḥuffāẓ (memorizers of the Qur\'ān) were martyred. Umar RA expressed concern to Abu Bakr that if more battles occurred and more memorizers were lost, portions of the Qur\'ān might be lost. Though initially reluctant because the Prophet ﷺ hadn\'t done this, Abu Bakr eventually agreed it was necessary.',
    topic: 'qur101'
  },
  {
    id: 'qur079',
    text: 'Why was Zaid ibn Thābit chosen to compile the Qur\'ān during Abu Bakr\'s time?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'He was passionate and reasonable', isCorrect: true },
      { id: 'b', text: 'He was trustworthy with no suspicions against him', isCorrect: true },
      { id: 'c', text: 'He had served as a scribe for the Prophet ﷺ', isCorrect: true },
      { id: 'd', text: 'He was the oldest companion alive', isCorrect: false }
    ],
    explanation: 'Zaid ibn Thābit was chosen to compile the Qur\'ān during Abu Bakr\'s time because he was passionate, reasonable, and completely trustworthy (with no suspicions against him). Additionally, he had served as a scribe for the Prophet ﷺ during revelations, giving him unique expertise in recording the Qur\'ān accurately.',
    topic: 'qur101'
  },
  {
    id: 'qur080',
    text: 'What rigorous process did Zaid ibn Thābit follow to verify Qur\'ānic manuscripts?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Required witnesses present at the time of revelation', isCorrect: true },
      { id: 'b', text: 'Corroborated documentation from multiple sources', isCorrect: true },
      { id: 'c', text: 'Confirmed trustworthiness of sources', isCorrect: true },
      { id: 'd', text: 'Consulted with non-Muslim scholars for objectivity', isCorrect: false }
    ],
    explanation: 'Zaid ibn Thābit followed an extremely rigorous process to verify Qur\'ānic manuscripts: he required witnesses who were present at the time of revelation, corroborated documentation from multiple sources, and confirmed the trustworthiness of all sources. This meticulous approach ensured the authenticity and accuracy of the compiled Qur\'ān.',
    topic: 'qur101'
  }
];
